import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acknowledgeRules,
  fetchCurrentWeek,
  fetchMyPools,
  fetchPoolBySlug,
  fetchRulesAcknowledgement,
  fetchTiebreakerMethod,
} from './api'

export function useMyPools() {
  return useQuery({ queryKey: ['pools'], queryFn: fetchMyPools })
}

export function usePool(slug: string | undefined) {
  return useQuery({
    queryKey: ['pool', slug],
    queryFn: () => fetchPoolBySlug(slug!),
    enabled: Boolean(slug),
  })
}

export function useTiebreakerMethod(methodId: string | undefined) {
  return useQuery({
    queryKey: ['tiebreakerMethod', methodId],
    queryFn: () => fetchTiebreakerMethod(methodId!),
    enabled: Boolean(methodId),
    // Reference data seeded by 0004 and not user-editable.
    staleTime: Infinity,
  })
}

export function useRulesAcknowledgement(poolId: string | undefined) {
  return useQuery({
    queryKey: ['rulesAck', poolId],
    queryFn: () => fetchRulesAcknowledgement(poolId!),
    enabled: Boolean(poolId),
  })
}

export function useAcknowledgeRules(poolId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rulesVersion: string) => acknowledgeRules(poolId!, rulesVersion),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rulesAck', poolId] })
    },
  })
}

export function useCurrentWeek(seasonId: number | undefined) {
  return useQuery({
    queryKey: ['week', 'current', seasonId],
    queryFn: () => fetchCurrentWeek(seasonId!),
    enabled: Boolean(seasonId),
    // Re-check on focus so a week that locked while the tab was backgrounded
    // does not stay open in the UI.
    staleTime: 60_000,
  })
}
